import * as React from "react";
import MetricsCards from '../MetricsCards';
import { ExportButton, RefreshDataButton } from '../shared';
import GoogleMapDataLayer from './GoogleMapDataLayer';
import CityMapView from './CityMapView';
import ZipcodeMapView from './ZipcodeMapView';
import StatePerformanceTable from './StatePerformanceTable';
import CityPerformanceTable from './CityPerformanceTable';
import ZipcodePerformanceTable from './ZipcodePerformanceTable';
import type { MetricData } from '../MetricsCards';
import type {
  GeographicAnalyticsDashboardProps,
  GeographicFilters,
  GeographicDimension,
  UserType,
  StateData,
  CityData,
  ZipcodeData,
  GeographicMetricsSummary,
  GeographicROIResponse,
  GeographicLoadingState,
  GeographicError,
  GeographicAnalyticsData,
  MapColorScale
} from '../../types/geographic';

// Default configuration
const DEFAULT_CONFIG = {
  INITIAL_FILTERS: {
    dimension: 'states' as GeographicDimension,
    userType: 'clients' as UserType,
    timeFrame: '30' as const,
    includeROI: true
  },
  REFRESH_INTERVAL: 300000, // 5 minutes
  MAP_COLORS: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#08519c', '#08306b'],
  CHART_COLORS: {
    primary: '#0d6efd',
    success: '#198754',
    info: '#0dcaf0',
    warning: '#ffc107',
    danger: '#dc3545'
  }
} as const;

/**
 * GeographicAnalyticsDashboard Component
 * 
 * A comprehensive geographic analytics dashboard for visualizing user distribution,
 * performance metrics, and ROI data across states, cities, and zipcodes.
 * 
 * Features:
 * - Interactive US states map visualization using Google Maps Data Layer
 * - City and zipcode maps using Google Maps markers
 * - Data tables for states, cities, and zipcodes
 * - Both map and table views displayed simultaneously
 * - User type filtering (clients vs professionals)
 * - ROI metrics integration
 * - Real-time data updates
 * - Export functionality
 * - Responsive Bootstrap 5 layout
 * 
 * Usage:
 * ```tsx
 * <GeographicAnalyticsDashboard
 *   apiEndpoint="/admin/analytics/geographic"
 *   enableRealTimeUpdates={true}
 *   refreshInterval={60000}
 * />
 * ```
 */
const GeographicAnalyticsDashboard: React.FC<GeographicAnalyticsDashboardProps> = ({
  className = "",
  apiEndpoint = "/admin/analytics/geographic",
  initialFilters = {},
  refreshInterval = DEFAULT_CONFIG.REFRESH_INTERVAL,
  enableRealTimeUpdates = false,
  googleMapsApiKey,
  enableGoogleMaps = true
}) => {
  // State management
  const [filters, setFilters] = React.useState<GeographicFilters>(() => ({
    ...DEFAULT_CONFIG.INITIAL_FILTERS,
    ...initialFilters
  }));

  const [analyticsData, setAnalyticsData] = React.useState<GeographicAnalyticsData | null>(null);
  const [loading, setLoading] = React.useState<GeographicLoadingState>({
    dashboard: true,
    map: true,
    table: true,
    metrics: true,
    roi: true
  });
  const [error, setError] = React.useState<GeographicError | null>(null);
  const [isExporting, setIsExporting] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date());
  // Always use Google Maps (removed SVG toggle)

  // Refs for cleanup
  const refreshIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // Calculate color scale for map visualization
  const mapColorScale = React.useMemo((): MapColorScale => {
    if (!analyticsData?.states.length) {
      return {
        min: 0,
        max: 100,
        colors: [...DEFAULT_CONFIG.MAP_COLORS],
        breaks: [0, 10, 25, 50, 100, 250, 500, 1000, 2000]
      };
    }

    const values = analyticsData.states.map(state => 
      filters.userType === 'clients' ? state.client_count : 
      filters.userType === 'professionals' ? state.professional_count : 
      state.user_count
    );

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    const breaks = [
      min,
      min + range * 0.1,
      min + range * 0.25,
      min + range * 0.5,
      min + range * 0.75,
      min + range * 0.9,
      max
    ];

    return {
      min,
      max,
      colors: [...DEFAULT_CONFIG.MAP_COLORS],
      breaks
    };
  }, [analyticsData?.states, filters.userType]);

  // Transform data for metrics cards
  const metricsData = React.useMemo((): MetricData[] => {
    if (!analyticsData?.metrics) return [];

    return [
      {
        id: 'total-users',
        label: 'Total Users',
        value: analyticsData.metrics.total_users,
        icon: 'fas fa-users',
        variant: 'primary',
        format: 'number',
        description: 'All user types'
      },
      {
        id: 'total-clients',
        label: 'Total Clients',
        value: analyticsData.metrics.total_clients,
        icon: 'fas fa-building',
        variant: 'success',
        format: 'number',
        description: 'Dental practices'
      },
      {
        id: 'total-professionals',
        label: 'Total Professionals',
        value: analyticsData.metrics.total_professionals,
        icon: 'fas fa-user-md',
        variant: 'info',
        format: 'number',
        description: 'Dental professionals'
      },
      {
        id: 'average-roi',
        label: 'Average ROI',
        value: analyticsData.metrics.average_roi,
        icon: 'fas fa-chart-line',
        variant: 'warning',
        format: 'percentage',
        description: 'Return on investment'
      },
      {
        id: 'state-coverage',
        label: 'State Coverage',
        value: analyticsData.metrics.coverage_states,
        icon: 'fas fa-map',
        variant: 'primary',
        format: 'number',
        suffix: ' states',
        description: 'Geographic reach'
      },
      {
        id: 'total-revenue',
        label: 'Total Revenue',
        value: analyticsData.metrics.total_revenue,
        icon: 'fas fa-dollar-sign',
        variant: 'success',
        format: 'currency',
        description: 'Generated revenue'
      }
    ];
  }, [analyticsData?.metrics]);

  // Fetch analytics data
  const fetchAnalyticsData = React.useCallback(async (newFilters?: GeographicFilters) => {
    const activeFilters = newFilters || filters;
    
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading({ dashboard: true, map: true, table: true, metrics: true, roi: true });

      const params = new URLSearchParams({
        dimension: activeFilters.dimension,
        user_type: activeFilters.userType,
        time_frame: activeFilters.timeFrame,
        include_roi: activeFilters.includeROI ? 'true' : 'false',
        page: '1',
        per_page: '100',
        ...(activeFilters.dateRange && {
          start_date: activeFilters.dateRange.start,
          end_date: activeFilters.dateRange.end
        }),
        ...(activeFilters.minUserCount && {
          min_user_count: activeFilters.minUserCount.toString()
        }),
        ...(activeFilters.states?.length && {
          states: activeFilters.states.join(',')
        })
      });

      // Get CSRF token for Rails API requests
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      const fetchOptions = {
        signal: abortController.signal,
        credentials: 'same-origin' as RequestCredentials,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        }
      };

      // Fetch all required data in parallel
      const usersUrl = `${apiEndpoint}/users_by_${activeFilters.dimension === 'states' ? 'state' : activeFilters.dimension === 'cities' ? 'city' : 'zipcode'}?${params}`;
      
      // ROI endpoint expects region_type instead of dimension
      const roiParams = new URLSearchParams({
        region_type: activeFilters.dimension === 'states' ? 'state' : activeFilters.dimension === 'cities' ? 'city' : 'zipcode',
        user_type: activeFilters.userType,
        time_frame: activeFilters.timeFrame,
        ...(activeFilters.dateRange && {
          start_date: activeFilters.dateRange.start,
          end_date: activeFilters.dateRange.end
        })
      });
      
      const roiUrl = `${apiEndpoint}/roi_by_region?${roiParams}`;
      const metricsUrl = `${apiEndpoint}/metrics_summary?${params}`;
      
      const [usersResponse, roiResponse, metricsResponse] = await Promise.all([
        fetch(usersUrl, fetchOptions),
        activeFilters.includeROI ? fetch(roiUrl, fetchOptions) : Promise.resolve(null),
        fetch(metricsUrl, fetchOptions)
      ]);

      // Check for errors with detailed status information
      if (!usersResponse.ok) {
        const errorText = await usersResponse.text();
        console.error('Users API Error:', usersResponse.status, errorText);
        throw new Error(`Users data: HTTP ${usersResponse.status} - ${errorText.substring(0, 100)}`);
      }
      if (roiResponse && !roiResponse.ok) {
        const errorText = await roiResponse.text();
        console.error('ROI API Error:', roiResponse.status, errorText);
        throw new Error(`ROI data: HTTP ${roiResponse.status} - ${errorText.substring(0, 100)}`);
      }
      if (!metricsResponse.ok) {
        const errorText = await metricsResponse.text();
        console.error('Metrics API Error:', metricsResponse.status, errorText);
        throw new Error(`Metrics data: HTTP ${metricsResponse.status} - ${errorText.substring(0, 100)}`);
      }

      // Parse responses
      const usersData = await usersResponse.json();
      const roiData = roiResponse ? await roiResponse.json() : { regions: [], summary: {} };
      const metricsData = await metricsResponse.json();

      // Validate API response structure
      if (!Array.isArray(usersData?.data)) {
        throw new Error('Invalid users data structure received from API');
      }

      // Tables search and sort locally, so provide every API page. Limit concurrent
      // requests to three and cancel the whole load when the filters change.
      const userRows = [...usersData.data];
      const totalPages = Number(usersData.pagination?.total_pages || 1);
      for (let firstPage = 2; firstPage <= totalPages; firstPage += 3) {
        if (abortController.signal.aborted) return;
        const pages = Array.from({ length: Math.min(3, totalPages - firstPage + 1) }, (_, index) => firstPage + index);
        const responses = await Promise.all(pages.map(async page => {
          const pageParams = new URLSearchParams(params);
          pageParams.set('page', page.toString());
          const response = await fetch(`${usersUrl.split('?')[0]}?${pageParams}`, fetchOptions);
          if (!response.ok) throw new Error(`Users data: HTTP ${response.status} on page ${page}`);
          const payload = await response.json();
          if (!Array.isArray(payload.data)) throw new Error('Invalid users data structure received from API');
          return payload.data;
        }));
        responses.forEach(rows => userRows.push(...rows));
      }
      if (abortController.signal.aborted) return;

      // Transform and combine data based on dimension
      let statesData = [];
      let citiesData = [];
      let zipcodesData = [];
      
      // The API returns { data: [...], metadata: {...} }
      if (activeFilters.dimension === 'states') {
        statesData = userRows;
      } else if (activeFilters.dimension === 'cities') {
        citiesData = userRows;
      } else if (activeFilters.dimension === 'zipcodes') {
        zipcodesData = userRows;
      }
      
      const transformedData: GeographicAnalyticsData = {
        states: statesData,
        cities: citiesData,
        zipcodes: zipcodesData,
        metrics: metricsData,
        roi: roiData.data || roiData,
        lastUpdated: new Date()
      };

      setAnalyticsData(transformedData);
      setError(null);
      setLastUpdated(new Date());
      
    } catch (err: any) {
      if (err.name !== 'AbortError' && !abortController.signal.aborted) {
        console.error('Geographic analytics data fetch error:', err);
        const errorDetails: GeographicError = {
          type: err.message.includes('HTTP') ? 'server' : 'network',
          message: err.message || 'Failed to load geographic analytics data',
          timestamp: new Date()
        };
        setError(errorDetails);
      }
    } finally {
      if (abortControllerRef.current === abortController) setLoading({
        dashboard: false,
        map: false,
        table: false,
        metrics: false,
        roi: false
      });
    }
  }, [filters, apiEndpoint]);

  // Handle filter changes
  const handleFiltersChange = React.useCallback((newFilters: Partial<GeographicFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
  }, [filters]);

  // Handle dimension toggle
  const handleDimensionChange = React.useCallback((dimension: GeographicDimension) => {
    handleFiltersChange({ dimension, ...(dimension === 'states' && { states: [] }) });
  }, [handleFiltersChange]);

  // Handle user type toggle
  const handleUserTypeChange = React.useCallback((userType: UserType) => {
    handleFiltersChange({ userType });
  }, [handleFiltersChange]);


  // Map type toggle removed - always use Google Maps

  // Handle state click in map
  const handleStateClick = React.useCallback((state: StateData) => {
    handleFiltersChange({ dimension: 'cities', states: [state.state_code] });
  }, [handleFiltersChange]);

  // Handle refresh
  const handleRefresh = React.useCallback(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Handle clear cache
  const handleClearCache = React.useCallback(async () => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      const response = await fetch(`${apiEndpoint}/clear_cache`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        }
      });

      if (!response.ok) {
        throw new Error(`Clear cache failed: HTTP ${response.status}`);
      }

      await response.json();
      
      // Refresh data after clearing cache
      await fetchAnalyticsData();
    } catch (err) {
      console.error('Clear cache error:', err);
      // Still try to refresh even if cache clear failed
      fetchAnalyticsData();
    }
  }, [apiEndpoint, fetchAnalyticsData]);

  // Handle export
  const handleExport = React.useCallback(async () => {
    if (!analyticsData || loading.dashboard || isExporting) return;

    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        dimension: filters.dimension,
        user_type: filters.userType,
        format: 'csv',
        ...(filters.states?.length && { states: filters.states.join(',') })
      });

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      const response = await fetch(`${apiEndpoint}/export?${params}`, {
        credentials: 'same-origin',
        headers: { 
          'Accept': 'text/csv',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `geographic-analytics-${filters.dimension}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      console.error('Export error:', err);
      setError({ message: 'Could not export geographic data. Please try again.', type: 'server', timestamp: new Date() });
    } finally {
      setIsExporting(false);
    }
  }, [analyticsData, filters, apiEndpoint, loading.dashboard, isExporting]);

  // Get current data based on dimension
  const getCurrentData = React.useCallback(() => {
    if (!analyticsData) return [];
    
    switch (filters.dimension) {
      case 'states':
        return analyticsData.states;
      case 'cities':
        return analyticsData.cities;
      case 'zipcodes':
        return analyticsData.zipcodes;
      default:
        return analyticsData.states;
    }
  }, [analyticsData, filters.dimension]);

  // Initial data load
  React.useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Set up refresh interval if real-time updates are enabled
  React.useEffect(() => {
    if (enableRealTimeUpdates && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchAnalyticsData();
      }, refreshInterval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [enableRealTimeUpdates, refreshInterval, fetchAnalyticsData]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className={`geographic-analytics-dashboard ${className}`.trim()}>
      {/* Report actions */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <small className="text-muted">
                Last updated: {lastUpdated.toLocaleString()}
                {enableRealTimeUpdates && (
                  <span className="ms-2">
                    <i className="fas fa-sync-alt me-1" aria-hidden="true"></i>
                    Auto-refresh enabled
                  </span>
                )}
              </small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <RefreshDataButton
                onRefresh={handleRefresh}
                onClearCache={handleClearCache}
                isRefreshing={loading.dashboard}
                lastRefreshed={lastUpdated}
              />
              <ExportButton
                onExport={handleExport}
                formats={['csv']}
                disabled={loading.dashboard || !analyticsData}
                isExporting={isExporting}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col">
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Error loading geographic data:</strong> {error.message}
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-3"
                onClick={handleRefresh}
              >
                <i className="fas fa-redo me-1" aria-hidden="true"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control Panel */}
      <div className="row mb-4">
        <div className="col">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-center">
                {/* Dimension Toggle */}
                <div className="col-lg-3 col-md-6 mb-3 mb-lg-0">
                  <label className="form-label fw-semibold mb-2">
                    <i className="fas fa-layer-group me-1" aria-hidden="true"></i>
                    Geographic Level
                  </label>
                  <div className="btn-group w-100" role="group">
                    {(['states', 'cities', 'zipcodes'] as GeographicDimension[]).map(dimension => (
                      <button
                        key={dimension}
                        type="button"
                        className={`btn btn-sm ${filters.dimension === dimension ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDimensionChange(dimension)}
                      >
                        {dimension.charAt(0).toUpperCase() + dimension.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* User Type Toggle */}
                <div className="col-lg-3 col-md-6 mb-3 mb-lg-0">
                  <label className="form-label fw-semibold mb-2">
                    <i className="fas fa-users me-1" aria-hidden="true"></i>
                    User Type
                  </label>
                  <div className="btn-group w-100" role="group">
                    {(['clients', 'professionals'] as UserType[]).map(userType => (
                      <button
                        key={userType}
                        type="button"
                        className={`btn btn-sm ${filters.userType === userType ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => handleUserTypeChange(userType)}
                      >
                        {userType.charAt(0).toUpperCase() + userType.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className={`col-lg-6 col-md-12`}>
                  <label className="form-label fw-semibold mb-2">
                    <i className="fas fa-chart-bar me-1" aria-hidden="true"></i>
                    Quick Stats
                  </label>
                  <div className="small text-muted">
                    <div>Total Records: {getCurrentData().length}</div>
                    <div>
                      {filters.userType === 'clients' ? 'Total Clients' : 
                       filters.userType === 'professionals' ? 'Total Professionals' : 
                       'Total Users'}: {analyticsData?.metrics ? 
                        ((filters.userType === 'clients' ? analyticsData.metrics.total_clients :
                         filters.userType === 'professionals' ? analyticsData.metrics.total_professionals :
                         analyticsData.metrics.total_users) || 0).toLocaleString() : '0'}
                    </div>
                  </div>
                </div>
              </div>
              {!!filters.states?.length && (
                <div className="mt-3 d-flex align-items-center gap-2">
                  <span>Showing locations in {filters.states.join(', ')}</span>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleFiltersChange({ states: [] })}>
                    Show all states
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="row mb-4">
        <div className="col">
          <MetricsCards
            metrics={metricsData}
            isLoading={loading.metrics}
            animateOnMount={true}
            showTrends={false}
          />
        </div>
      </div>

      {/* Main Content Area - Map and Table Side by Side */}
      <div className="row">
        {/* Map Section */}
        <div className="col-lg-12 mb-4">
          {filters.dimension === 'states' && (
            <GoogleMapDataLayer
              data={analyticsData?.states || []}
              userType={filters.userType}
              colorScale={mapColorScale}
              onStateClick={handleStateClick}
              isLoading={loading.map}
              googleMapsApiKey={googleMapsApiKey}
            />
          )}
          {filters.dimension === 'cities' && (
            <CityMapView
              data={analyticsData?.cities || []}
              userType={filters.userType}
              isLoading={loading.map}
              googleMapsApiKey={googleMapsApiKey}
            />
          )}
          {filters.dimension === 'zipcodes' && (
            <ZipcodeMapView
              data={analyticsData?.zipcodes || []}
              userType={filters.userType}
              isLoading={loading.map}
              googleMapsApiKey={googleMapsApiKey}
            />
          )}
        </div>

        {/* Table Section */}
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-table me-2" aria-hidden="true"></i>
                {filters.dimension.charAt(0).toUpperCase() + filters.dimension.slice(1)} Performance Data
              </h5>
            </div>
            <div className="card-body">
              {filters.dimension === 'states' && (
                <StatePerformanceTable
                  data={analyticsData?.states || []}
                  isLoading={loading.table}
                  pageSize={25}
                />
              )}
              {filters.dimension === 'cities' && (
                <CityPerformanceTable
                  key={`${filters.userType}-${filters.states?.join(',') || 'all'}`}
                  data={analyticsData?.cities || []}
                  isLoading={loading.table}
                  pageSize={25}
                />
              )}
              {filters.dimension === 'zipcodes' && (
                <ZipcodePerformanceTable
                  key={`${filters.userType}-${filters.states?.join(',') || 'all'}`}
                  data={analyticsData?.zipcodes || []}
                  isLoading={loading.table}
                  pageSize={25}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

GeographicAnalyticsDashboard.displayName = 'GeographicAnalyticsDashboard';

export default GeographicAnalyticsDashboard;
export type { GeographicAnalyticsDashboardProps };
